import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { cloneDeep, get, omit, set } from 'lodash';
import AuthLoader from '@lendi-ui/auth-loader';

import {
  COMPANY_TYPE,
  FIELD_PATHS,
  INCOME_TYPE,
  PERSON_TYPE,
  TEAM_VIEW_BASE_NAME,
  URL_PARAM_APPLICATION_ID,
  URL_PARAM_CUSTOMER_ID,
  VISIT_CONTACT_TYPE,
} from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { clearAllData, persistApplicationDataCollection } from '@funnels/redux-store';
import {
  APPLICATION_DATA_COLLECTION_STATUS,
  Asset,
  BusinessIncomeDetails,
  Company,
  Expenses,
  Income,
  Liability,
  ReduxStoreData,
  RelatedParty,
  StoreApplicant,
  StoreEmployment,
  UseParams,
} from '@funnels/types';

import { HydrationProviderForClone } from './HydrationProviderForClone';

function mapState(currentState: ReduxStoreData) {
  const data = {
    applications: currentState.applications.data,
    applicants: currentState.applicants.data,
    assets: currentState.assets.data,
    companies: currentState.companies.data,
    employments: currentState.employments.data,
    expenses: currentState.expenses.data,
    incomes: currentState.incomes.data,
    liabilities: currentState.liabilities.data,
    relatedParties: currentState.relatedParties.data,
  };
  const errors = {
    applications: currentState.applications.error,
    applicants: currentState.applicants.error,
    assets: currentState.assets.error,
    companies: currentState.companies.error,
    employments: currentState.employments.error,
    expenses: currentState.expenses.error,
    incomes: currentState.incomes.error,
    liabilities: currentState.liabilities.error,
    relatedParties: currentState.relatedParties.error,
  };
  const loading =
    currentState.applications.loading +
    currentState.applicants.loading +
    currentState.assets.loading +
    currentState.companies.loading +
    currentState.employments.loading +
    currentState.expenses.loading +
    currentState.incomes.loading +
    currentState.liabilities.loading +
    currentState.relatedParties.loading;
  const customer = currentState.customers.data[0];
  const status = currentState.applicationDataCollection.status;

  return { data, errors, loading, customer, status };
}

function Clone() {
  const { data, status, customer, errors } = useSelector(mapState);
  const { tracking } = useAnalytics();

  const { [URL_PARAM_APPLICATION_ID]: applicationId, [URL_PARAM_CUSTOMER_ID]: customerId } = useParams<UseParams>();
  const history = useHistory();
  const dispatch = useDispatch();
  const newData = useMemo(() => {
    const clonedData = cloneDeep(data);
    const applicationCustomerId = get(clonedData, FIELD_PATHS.APPLICATION_CUSTOMER_ID(0));
    const newApplication = (() => {
      const application = omit(get(clonedData, FIELD_PATHS.APPLICATION(0)), [
        'id',
        'createdAt',
        'updatedAt',
        'isDeleted',
        'isDeidentified',
        'deidentificationMeta',
        'hasOptedInRateLock',
        'isFastRefi',
      ]);
      const name = get(clonedData, FIELD_PATHS.APPLICATION_NAME(0));

      set({ applications: [application] }, FIELD_PATHS.APPLICATION_CLONED_FROM_APPLICATION_ID(0), applicationId);
      set({ applications: [application] }, FIELD_PATHS.APPLICATION_NAME(0), `${name} (copy)`);

      return application;
    })();
    const newApplicants = get(clonedData, FIELD_PATHS.APPLICANTS(), []).map((applicant: StoreApplicant) => {
      const propsToOmit = [
        'id',
        'applicationId',
        'createdAt',
        'updatedAt',
        'isDeleted',
        'isDeidentified',
        'deidentificationMeta',
        'invitationStatus',
      ];
      const applicantId = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ID(0));
      set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_CLONED_FROM_APPLICANT_ID(0), applicantId);

      const isSecondaryApplicant =
        get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_CUSTOMER_ID(0)) !== applicationCustomerId;

      if (isSecondaryApplicant) {
        propsToOmit.push('customerId');
      }

      return omit(applicant, propsToOmit);
    });
    const newAssets = get(clonedData, FIELD_PATHS.ASSETS(), []).map((a: Asset) => {
      const asset = omit(a, [
        'id',
        'applicationId',
        'createdAt',
        'updatedAt',
        'relationId',
        'isDeidentified',
        'isDeleted',
      ]);

      set({ assets: [asset] }, FIELD_PATHS.ASSET_LIABILITY_CLIENT_ID(0), []);
      set({ assets: [asset] }, FIELD_PATHS.ASSET_LIABILITY_ID(0), []);

      return asset;
    });
    const newLiabilities = get(clonedData, FIELD_PATHS.LIABILITIES(), []).map((l: Liability) => {
      const liability = omit(l, [
        'id',
        'applicationId',
        'createdAt',
        'updatedAt',
        'relationId',
        'isDeidentified',
        'isDeleted',
      ]);
      const interestRate = get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_INTEREST_RATE(0));
      // interest Rate over 99.99 was allowed, here is a patch for those old invalid rates
      interestRate > 99.99 && set({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_INTEREST_RATE(0), undefined);

      set({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_ASSET_ID(0), []);

      return liability;
    });
    const newEmployments = get(clonedData, FIELD_PATHS.EMPLOYMENTS(), []).map((e: StoreEmployment) => {
      const employment = omit(e, ['id', 'applicationId', 'createdAt', 'updatedAt']);

      return employment;
    });
    const newIncomes = get(clonedData, FIELD_PATHS.INCOMES(), []).map((i: Income) => {
      const income = omit(i, [
        'id',
        'applicationId',
        'createdAt',
        'updatedAt',
        'applicantId',
        'isDeidentified',
        'isDeleted',
      ]);
      const incomeType = get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0));
      // SE v2 full doc incomes
      if (incomeType === INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC) {
        const fullDocIncomes = get({ incomes: [income] }, FIELD_PATHS.INCOME_BUSINESS_INCOMES(0), []);
        // just keep recent 3yr full doc incomes
        const recent3yrIncomes = fullDocIncomes.filter((fullDocIncome: BusinessIncomeDetails) => {
          const financialYear = get(
            { incomes: [{ incomes: [fullDocIncome] }] },
            FIELD_PATHS.INCOME_BUSINESS_INCOME_YEAR(0, 0)
          );
          const previous3Year = new Date().getFullYear() - 3;
          return financialYear >= previous3Year;
        });
        set({ incomes: [income] }, FIELD_PATHS.INCOME_BUSINESS_INCOMES(0), recent3yrIncomes);
      }
      return income;
    });
    const newExpenses = get(clonedData, FIELD_PATHS.EXPENSES(), []).map((e: Expenses) => {
      const expenses = omit(e, ['id', 'applicationId', 'createdAt', 'updatedAt', 'isDeidentified', 'isDeleted']);

      return expenses;
    });

    const newCompanies = get(clonedData, FIELD_PATHS.COMPANIES(), []).map((c: Company) => {
      const company = omit(c, ['id', 'applicationId', 'createdAt', 'updatedAt', 'isDeidentified', 'isDeleted']);

      return company;
    });

    // Currently copy for Spouse Details, Accountant details, Property valuation access,
    // Conveyancer details, Licensed builder details
    const newRelatedParties = get(clonedData, FIELD_PATHS.RELATED_PARTIES(), []).reduce(
      (relatedParties: RelatedParty[], r: RelatedParty) => {
        const copyType = [
          PERSON_TYPE.Spouse,
          COMPANY_TYPE.Accountant,
          COMPANY_TYPE.Solicitor,
          COMPANY_TYPE.LicensedBuilder,
          VISIT_CONTACT_TYPE.LoanParty,
          VISIT_CONTACT_TYPE.RealEstateAgent,
          VISIT_CONTACT_TYPE.Tenant,
        ];
        const type = get({ relatedParties: [r] }, FIELD_PATHS.RELATED_PARTY_TYPE(0));

        if (copyType.includes(type)) {
          const relatedParty = omit(r, [
            'id',
            'applicationId',
            'createdAt',
            'updatedAt',
            'isDeidentified',
            'isDeleted',
          ]) as RelatedParty;
          relatedParties.push(relatedParty);
        }
        return relatedParties;
      },
      []
    );

    return {
      applications: [newApplication],
      applicants: newApplicants,
      assets: newAssets,
      companies: newCompanies,
      liabilities: newLiabilities,
      employments: newEmployments,
      expenses: newExpenses,
      incomes: newIncomes,
      relatedParties: newRelatedParties,
    };
  }, []);
  const hasError =
    errors.applications ||
    errors.applicants ||
    errors.assets ||
    errors.companies ||
    errors.liabilities ||
    errors.employments ||
    errors.expenses ||
    errors.incomes ||
    errors.relatedParties;

  useEffect(() => {
    if (status === APPLICATION_DATA_COLLECTION_STATUS.INIT) {
      dispatch(clearAllData());
      dispatch(persistApplicationDataCollection(newData, true, customer, tracking!, true));
    } else if (status === APPLICATION_DATA_COLLECTION_STATUS.SUCCESS) {
      // const newApplicationId = data.applications[0].id;
      window.location.assign(`${TEAM_VIEW_BASE_NAME}/customer/${customerId}/applications/`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (hasError) {
      const errorType = errors.applications ? 1 : 2;

      window.location.assign(`${TEAM_VIEW_BASE_NAME}/customer/${customerId}/applications/?e=${errorType}`);
    }
  }, [hasError]);

  return <AuthLoader data-component="lui-auth-loader" />;
}

function CloneProvider() {
  return (
    <HydrationProviderForClone
      value={[
        'applications',
        'applicants',
        'assets',
        'companies',
        'liabilities',
        'employments',
        'expenses',
        'incomes',
        'brokerCurrentCustomer',
        'relatedParties',
      ]}
    >
      <Clone />
    </HydrationProviderForClone>
  );
}

export default CloneProvider;
